import React, { useState, useEffect, useMemo } from 'react'
import { 
  TableCellsIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  UserGroupIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import { apiService } from '../services/api'
import {
  getDateRange,
  getScheduleForDay,
  buildTimeSlots,
  getSlotDateTime,
  isDateClosed,
  isSlotTooSoon,
  type DaySchedule
} from '../utils/reservationSlots'

interface Table {
  id: string
  number: string
  capacity: number
  status: 'available' | 'occupied' | 'reserved' | 'cleaning'
  currentOrder?: Order
  reservation?: Reservation
  location: 'indoor' | 'outdoor' | 'bar'
  x: number
  y: number
  shape?: 'round' | 'square' | 'rectangle' | 'wall'
  width?: number
  height?: number
  rotation?: number
  floor_plan_id?: string
  createdAt: Date
  updatedAt: Date
}

  interface FloorPlanElement {
  id: string
  type: 'wall' | 'door' | 'entry' | 'exit'
  x1: number
  y1: number
  x2: number
  y2: number
  width?: number
  rotation?: number
  label?: string
  style?: Record<string, any>
  floor_plan_id?: string
}

interface Chair {
  id: string
  table_id: string
  x_position: number
  y_position: number
  rotation?: number
  chair_type?: 'standard' | 'barstool' | 'booth' | 'sofa' | 'back_chair'
}

interface Order {
  id: string
  tableId: string
  customerName?: string
  items: OrderItem[]
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'served' | 'completed'
  total: number
  createdAt: Date
  updatedAt: Date
  estimatedReadyTime?: Date
  notes?: string
}

interface OrderItem {
  id: string
  name: string
  quantity: number
  price: number
  status: 'pending' | 'preparing' | 'ready'
  notes?: string
}

interface Reservation {
  id: string
  tableId: string
  customerName: string
  customerPhone: string
  partySize: number
  reservationTime: Date
  status: 'confirmed' | 'seated' | 'cancelled' | 'no-show'
  notes?: string
  createdAt: Date
}


const TableManagement: React.FC = () => {
  const [tables, setTables] = useState<Table[]>([])
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)
  const [showTableModal, setShowTableModal] = useState(false)
  const [showOrderModal, setShowOrderModal] = useState(false)
  const [showReservationModal, setShowReservationModal] = useState(false)
  const [showReservationSuccessModal, setShowReservationSuccessModal] = useState(false)
  const [reservationError, setReservationError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [floorPlanFilter, setFloorPlanFilter] = useState<string>('all') // Filter for list/grid views
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'floorplan'>('floorplan')
  const [isEditingFloorPlan, setIsEditingFloorPlan] = useState(false)
  const [draggedTable, setDraggedTable] = useState<string | null>(null)
  const [floorPlanScale, setFloorPlanScale] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartPos, setDragStartPos] = useState<{ tableId: string; startX: number; startY: number; offsetX: number; offsetY: number } | null>(null)
  const [newTable, setNewTable] = useState<Partial<Table>>({})
  const [newReservation, setNewReservation] = useState<Partial<Reservation>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [restaurantSchedule, setRestaurantSchedule] = useState<Record<string, DaySchedule> | undefined>(undefined)
  const [restaurantHolidays, setRestaurantHolidays] = useState<string[]>([])
  const [slotDurationMinutes, setSlotDurationMinutes] = useState(30)
  const [minimumBookingHours, setMinimumBookingHours] = useState(2)

  // Floor plan drawing tools
  const [drawingTool, setDrawingTool] = useState<'none' | 'wall' | 'door' | 'entry' | 'exit' | 'chair' | 'table'>('none')
  const [floorPlanElements, setFloorPlanElements] = useState<FloorPlanElement[]>([])
  const [tableChairs, setTableChairs] = useState<Record<string, Chair[]>>({})
  const [drawingStart, setDrawingStart] = useState<{ x: number; y: number } | null>(null)
  const [drawingEnd, setDrawingEnd] = useState<{ x: number; y: number } | null>(null)
  
  // Floor plans and locations
  const [floorPlans, setFloorPlans] = useState<Array<{ id: string; name: string; level: number; description?: string; width: number; height: number }>>([])
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<string | null>(null)
  const [locations, setLocations] = useState<Array<{ id: string; name: string; description?: string; color: string }>>([])
  
  // Chair editing and organization
  const [selectedChair, setSelectedChair] = useState<{ chair: Chair; tableId: string } | null>(null)
  const [showChairModal, setShowChairModal] = useState(false)
  const [editingChair, setEditingChair] = useState<Partial<Chair>>({})
  const [draggedChair, setDraggedChair] = useState<{ chairId: string; tableId: string; startX: number; startY: number; mouseStartX: number; mouseStartY: number; offsetX: number; offsetY: number } | null>(null)
  const [organizingChairs, setOrganizingChairs] = useState<string | null>(null) // tableId that's being organized
  const [chairWasDragged, setChairWasDragged] = useState(false) // Track if chair was actually dragged vs just clicked
  const [unsavedChairChanges, setUnsavedChairChanges] = useState<Record<string, { x_position: number; y_position: number; tableId: string }>>({}) // chairId -> changes
  const [isSavingChairs, setIsSavingChairs] = useState(false)
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState<{ type: 'success' | 'error'; message: string }>({ type: 'success', message: '' })

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiService.getRestaurantSettings()
        if (res.success && res.data) {
          const d = res.data as any
          setRestaurantSchedule(d.restaurant_schedule ?? undefined)
          setRestaurantHolidays(Array.isArray(d.restaurant_holidays) ? d.restaurant_holidays : [])
          setSlotDurationMinutes(typeof d.reservation_timeslot_duration_minutes === 'number' ? d.reservation_timeslot_duration_minutes : 30)
          setMinimumBookingHours(Math.max(0, Number(d.reservation_minimum_booking_hours)) || 2)
        }
      } catch (e) {
        console.error('Failed to load restaurant settings for reservations', e)
      }
    }
    load()
  }, [])

  // Handle drag and drop for tables
  useEffect(() => {
    if (!isDragging || !dragStartPos) return

    const handleMouseMove = (e: MouseEvent) => {
      const container = document.querySelector('[data-floor-plan-container]') as HTMLElement
      if (!container) return
      
      const rect = container.getBoundingClientRect()
      const x = Math.max(0, Math.min(2000 - 100, (e.clientX - rect.left - dragStartPos.offsetX) / floorPlanScale))
      const y = Math.max(0, Math.min(1500 - 100, (e.clientY - rect.top - dragStartPos.offsetY) / floorPlanScale))
      
      setTables(prev => prev.map(t => 
        t.id === dragStartPos.tableId ? { ...t, x: Math.round(x), y: Math.round(y) } : t
      ))
    }

    const handleMouseUp = () => {
      if (dragStartPos) {
        // Get the current table state at the time of mouse up
        setTables(prev => {
          const updatedTable = prev.find(t => t.id === dragStartPos.tableId)
          if (updatedTable && (updatedTable.x !== dragStartPos.startX || updatedTable.y !== dragStartPos.startY)) {
            // Save position asynchronously
            handleUpdateTablePosition(dragStartPos.tableId, updatedTable.x, updatedTable.y)
          }
          return prev
        })
      }
      setIsDragging(false)
      setDraggedTable(null)
      setDragStartPos(null)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragStartPos, floorPlanScale])

  // Handle drag and drop for chairs
  useEffect(() => {
    if (!draggedChair) {
      setChairWasDragged(false)
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      const container = document.querySelector('[data-floor-plan-container]') as HTMLElement
      if (!container) return
      
      // Check if mouse moved significantly (more than 5px) to consider it a drag
      const deltaX = Math.abs(e.clientX - draggedChair.mouseStartX)
      const deltaY = Math.abs(e.clientY - draggedChair.mouseStartY)
      if (deltaX > 5 || deltaY > 5) {
        setChairWasDragged(true)
      }
      
      const table = tables.find(t => t.id === draggedChair.tableId)
      if (!table) return
      
      const rect = container.getBoundingClientRect()
      // Calculate mouse position in floor plan coordinates
      const mouseX = (e.clientX - rect.left) / floorPlanScale
      const mouseY = (e.clientY - rect.top) / floorPlanScale
      
      // Calculate table dimensions
      const tableWidth = table.shape === 'round' 
        ? (table.capacity <= 2 ? 64 : table.capacity <= 4 ? 80 : table.capacity <= 6 ? 96 : 112)
        : table.width || 80
      const tableHeight = table.shape === 'round'
        ? (table.capacity <= 2 ? 64 : table.capacity <= 4 ? 80 : table.capacity <= 6 ? 96 : 112)
        : table.height || 80
      
      // Calculate new chair position relative to table center
      // Account for the offset from where user clicked on the chair
      const x = mouseX - table.x - tableWidth / 2 - draggedChair.offsetX
      const y = mouseY - table.y - tableHeight / 2 - draggedChair.offsetY
      
      // Update chair position in state (only if it actually changed)
      setTableChairs(prev => {
        const currentChairs = prev[draggedChair.tableId] || []
        const currentChair = currentChairs.find(c => c.id === draggedChair.chairId)
        const newX = Math.round(x)
        const newY = Math.round(y)
        
        // Only update if position actually changed
        if (currentChair && (currentChair.x_position !== newX || currentChair.y_position !== newY)) {
          return {
            ...prev,
            [draggedChair.tableId]: currentChairs.map(c =>
              c.id === draggedChair.chairId ? { ...c, x_position: newX, y_position: newY } : c
            )
          }
        }
        return prev
      })
    }

    const handleMouseUp = () => {
      if (draggedChair) {
        const table = tables.find(t => t.id === draggedChair.tableId)
        if (table) {
          const updatedChair = tableChairs[draggedChair.tableId]?.find(c => c.id === draggedChair.chairId)
          if (updatedChair && chairWasDragged &&
              (updatedChair.x_position !== draggedChair.startX || updatedChair.y_position !== draggedChair.startY)) {
            // Store changes in memory (don't save to server yet)
            setUnsavedChairChanges(prev => ({
              ...prev,
              [draggedChair.chairId]: {
                x_position: updatedChair.x_position,
                y_position: updatedChair.y_position,
                tableId: draggedChair.tableId
              }
            }))
          } else if (!chairWasDragged) {
            // If chair wasn't dragged, reset to original position
            setTableChairs(prev => ({
              ...prev,
              [draggedChair.tableId]: (prev[draggedChair.tableId] || []).map(c =>
                c.id === draggedChair.chairId 
                  ? { ...c, x_position: draggedChair.startX, y_position: draggedChair.startY }
                  : c
              )
            }))
            // Remove from unsaved changes if it was reset
            setUnsavedChairChanges(prev => {
              const newChanges = { ...prev }
              delete newChanges[draggedChair.chairId]
              return newChanges
            })
          }
        }
      }
      // Reset after a short delay to allow onClick to check the flag
      setTimeout(() => {
        setDraggedChair(null)
        setChairWasDragged(false)
      }, 100)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [draggedChair, floorPlanScale, tables, tableChairs, chairWasDragged])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      await Promise.all([
        loadFloorPlans(),
        loadLocations(),
        loadTables(),
        loadReservations(),
        loadFloorPlanElements()
      ])
    } catch (err) {
      console.error('Error loading data:', err)
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const loadFloorPlans = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/floor-plans`)
      const data = await response.json()
      if (data.success && data.data && data.data.length > 0) {
        setFloorPlans(data.data)
        if (!selectedFloorPlanId) {
          setSelectedFloorPlanId(data.data[0].id)
        }
      }
    } catch (error) {
      console.error('Error loading floor plans:', error)
    }
  }

  const loadLocations = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/locations`)
      const data = await response.json()
      if (data.success) {
        setLocations(data.data || [])
      }
    } catch (error) {
      console.error('Error loading locations:', error)
    }
  }

  const loadFloorPlanElements = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const url = selectedFloorPlanId
        ? `${apiUrl}/floor-plan-elements?floor_plan_id=${selectedFloorPlanId}`
        : `${apiUrl}/floor-plan-elements`
      const response = await fetch(url)
      const data = await response.json()
      
      if (data.success && data.data) {
        setFloorPlanElements(data.data)
      }
    } catch (error) {
      console.error('Error loading floor plan elements:', error)
    }
  }

  const loadAllChairs = async (tableIds: string[] = [], preserveDragging: boolean = false) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const chairsMap: Record<string, Chair[]> = {}
      
      // Load chairs for each table
      for (const tableId of tableIds) {
        // Skip reloading if this table has a chair being dragged
        if (preserveDragging && draggedChair && draggedChair.tableId === tableId) {
          continue
        }
        
        const response = await fetch(`${apiUrl}/tables/${tableId}/chairs`)
        const data = await response.json()
        if (data.success && data.data) {
          chairsMap[tableId] = data.data
        }
      }
      
      // Only update chairs that weren't skipped, merge with existing state
      if (Object.keys(chairsMap).length > 0) {
        setTableChairs(prev => ({ ...prev, ...chairsMap }))
      }
    } catch (error) {
      console.error('Error loading chairs:', error)
    }
  }

  const loadTables = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const url = selectedFloorPlanId 
        ? `${apiUrl}/tables?floor_plan_id=${selectedFloorPlanId}`
        : `${apiUrl}/tables`
      const response = await fetch(url)
      const data = await response.json()
      
      if (data.success && data.data) {
        const transformedTables: Table[] = data.data.map((table: any) => ({
          id: table.id,
          number: table.number,
          capacity: table.capacity || 2,
          status: table.status || 'available',
          location: (table.location || 'indoor') as 'indoor' | 'outdoor' | 'bar',
          x: table.x_position || 0,
          y: table.y_position || 0,
          shape: table.shape || 'round',
          width: table.width || 80,
          height: table.height || 80,
          rotation: table.rotation || 0,
          floor_plan_id: table.floor_plan_id || undefined,
          createdAt: new Date(table.created_at),
          updatedAt: new Date(table.updated_at),
          currentOrder: table.current_order ? {
            id: table.current_order.id,
            tableId: table.id,
            customerName: table.current_order.customer_name,
            items: (table.current_order.order_items || []).map((item: any) => ({
              id: item.id,
              name: item.item_name,
              quantity: item.quantity,
              price: item.unit_price,
              status: item.status || 'pending'
            })),
            status: table.current_order.status || 'pending',
            total: table.current_order.total || 0,
            createdAt: new Date(table.current_order.created_at),
            updatedAt: new Date(table.current_order.created_at),
            estimatedReadyTime: table.current_order.estimated_ready_time ? new Date(table.current_order.estimated_ready_time) : undefined
          } : undefined,
          reservation: table.reservation ? {
            id: table.reservation.id,
            tableId: table.id,
            customerName: table.reservation.customer_name,
            customerPhone: table.reservation.customer_phone,
            partySize: table.reservation.party_size,
            reservationTime: new Date(table.reservation.reservation_time),
            status: table.reservation.status || 'confirmed',
            notes: table.reservation.notes,
            createdAt: new Date(table.reservation.created_at || table.reservation.reservation_time)
          } : undefined
        }))
        setTables(transformedTables)
        // Load chairs after tables are loaded
        await loadAllChairs(transformedTables.map(t => t.id))
      } else {
        setError(data.error || 'Failed to load tables')
      }
    } catch (error) {
      console.error('Error loading tables:', error)
      setError('Failed to load tables')
    }
  }

  const loadReservations = async () => {
    try {
      // Reservations are already included in tables data from the API
      // This function is kept for potential future use
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      await fetch(`${apiUrl}/reservations`)
    } catch (error) {
      console.error('Error loading reservations:', error)
    }
  }

  const handleAddTable = async () => {
    if (!newTable.number || !newTable.capacity || !newTable.location) {
      setNotificationMessage({ type: 'error', message: 'Please fill in all required fields' })
      setShowNotificationModal(true)
      return
    }

    setIsSaving(true)
    try {
      const tableData = {
        number: newTable.number,
        capacity: newTable.capacity,
        status: 'available',
        location: newTable.location,
        x_position: newTable.x || 0,
        y_position: newTable.y || 0,
        shape: newTable.shape || 'round',
        width: newTable.width || 80,
        height: newTable.height || 80,
        rotation: newTable.rotation || 0,
        floor_plan_id: newTable.floor_plan_id || selectedFloorPlanId || null,
        is_active: true
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/tables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tableData)
      })

      const data = await response.json()

      if (data.success) {
        setShowTableModal(false)
        setNewTable({})
        await loadTables()
        setNotificationMessage({ type: 'success', message: 'Table added successfully!' })
        setShowNotificationModal(true)
      } else {
        setNotificationMessage({ type: 'error', message: data.error || 'Failed to add table' })
        setShowNotificationModal(true)
      }
    } catch (error) {
      console.error('Error adding table:', error)
      setNotificationMessage({ type: 'error', message: 'Failed to add table' })
      setShowNotificationModal(true)
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpdateTablePosition = async (tableId: string, x: number, y: number) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/tables/${tableId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ x_position: x, y_position: y })
      })

      const data = await response.json()
      if (data.success) {
        await loadTables()
      }
    } catch (error) {
      console.error('Error updating table position:', error)
    }
  }

  const handleUpdateTable = async (table: Table) => {
    setIsSaving(true)
    try {
      // Do not send number on update (table number cannot be changed; avoids duplicate key errors)
      const updateData = {
        capacity: table.capacity,
        status: newTable.status || table.status,
        location: table.location,
        x_position: newTable.x !== undefined ? newTable.x : table.x,
        y_position: newTable.y !== undefined ? newTable.y : table.y,
        shape: newTable.shape !== undefined ? newTable.shape : table.shape,
        width: newTable.width !== undefined ? newTable.width : table.width,
        height: newTable.height !== undefined ? newTable.height : table.height,
        rotation: newTable.rotation !== undefined ? newTable.rotation : table.rotation,
        floor_plan_id: newTable.floor_plan_id !== undefined ? newTable.floor_plan_id : table.floor_plan_id
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/tables/${table.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      })

      const data = await response.json()

      if (data.success) {
        setShowTableModal(false)
        setSelectedTable(null)
        await loadTables()
        setNotificationMessage({ type: 'success', message: 'Table updated successfully!' })
        setShowNotificationModal(true)
      } else {
        setNotificationMessage({ type: 'error', message: data.error || 'Failed to update table' })
        setShowNotificationModal(true)
      }
    } catch (error) {
      console.error('Error updating table:', error)
      setNotificationMessage({ type: 'error', message: 'Failed to update table' })
      setShowNotificationModal(true)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteTable = async (tableId: string) => {
    if (!confirm('Are you sure you want to delete this table?')) {
      return
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/tables/${tableId}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        await loadTables()
        setNotificationMessage({ type: 'success', message: 'Table deleted successfully!' })
        setShowNotificationModal(true)
      } else {
        setNotificationMessage({ type: 'error', message: data.error || 'Failed to delete table' })
        setShowNotificationModal(true)
      }
    } catch (error) {
      console.error('Error deleting table:', error)
      setNotificationMessage({ type: 'error', message: 'Failed to delete table' })
      setShowNotificationModal(true)
    }
  }

  const dateRange = useMemo(() => getDateRange(), [])
  const reservationDate = newReservation.reservationTime
    ? new Date(newReservation.reservationTime.getFullYear(), newReservation.reservationTime.getMonth(), newReservation.reservationTime.getDate())
    : null
  const reservationTimeSlot = newReservation.reservationTime
    ? `${String(newReservation.reservationTime.getHours()).padStart(2, '0')}:${String(newReservation.reservationTime.getMinutes()).padStart(2, '0')}`
    : ''
  const selectedDaySchedule = reservationDate ? getScheduleForDay(restaurantSchedule, reservationDate.getDay()) : null
  const selectedDateClosed = reservationDate ? isDateClosed(reservationDate, restaurantSchedule, restaurantHolidays) : true
  const timeSlotsForSelectedDate =
    reservationDate && !selectedDateClosed && selectedDaySchedule
      ? buildTimeSlots(slotDurationMinutes, selectedDaySchedule.open, selectedDaySchedule.close)
      : []
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const setNewReservationDateAndTime = (date: Date, slot: string) => {
    const daySchedule = getScheduleForDay(restaurantSchedule, date.getDay())
    const d = getSlotDateTime(date, slot, daySchedule)
    setNewReservation({ ...newReservation, reservationTime: d })
  }

  const handleAddReservation = async () => {
    if (!newReservation.customerName || !newReservation.customerPhone || !newReservation.partySize || !newReservation.reservationTime) {
      setNotificationMessage({ type: 'error', message: 'Please fill in all required fields' })
      setShowNotificationModal(true)
      return
    }

    setIsSaving(true)
    try {
      const reservationData = {
        table_id: newReservation.tableId || null,
        customer_name: newReservation.customerName,
        customer_phone: newReservation.customerPhone,
        party_size: newReservation.partySize,
        reservation_time: newReservation.reservationTime instanceof Date 
          ? newReservation.reservationTime.toISOString() 
          : new Date(newReservation.reservationTime as any).toISOString(),
        status: 'confirmed',
        notes: newReservation.notes || null
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData)
      })

      const data = await response.json()

      if (data.success) {
        setShowReservationModal(false)
        setNewReservation({})
        await loadData()
        setShowReservationSuccessModal(true)
      } else {
        setReservationError(data.error || 'Failed to create reservation')
      }
    } catch (error) {
      console.error('Error creating reservation:', error)
      setReservationError('Failed to create reservation')
    } finally {
      setIsSaving(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'occupied': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'reserved': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'cleaning': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return '✅'
      case 'occupied': return '🍽️'
      case 'reserved': return '📅'
      case 'cleaning': return '🧹'
      default: return '❓'
    }
  }

  const handleCreateFloorPlanElement = async (element: Omit<FloorPlanElement, 'id'>) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/floor-plan-elements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(element)
      })

      const data = await response.json()
      if (data.success) {
        await loadFloorPlanElements()
      }
      return data
    } catch (error) {
      console.error('Error creating floor plan element:', error)
      return { success: false, error: 'Failed to create element' }
    }
  }

  const handleDeleteFloorPlanElement = async (id: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/floor-plan-elements/${id}`, {
        method: 'DELETE'
      })

      const data = await response.json()
      if (data.success) {
        await loadFloorPlanElements()
      }
      return data
    } catch (error) {
      console.error('Error deleting floor plan element:', error)
      return { success: false, error: 'Failed to delete element' }
    }
  }

  const handleCreateChair = async (tableId: string, chair: Omit<Chair, 'id' | 'table_id'>) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/tables/${tableId}/chairs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(chair)
      })

      const data = await response.json()
      if (data.success && data.data) {
        // Add the new chair to state immediately instead of reloading
        setTableChairs(prev => ({
          ...prev,
          [tableId]: [...(prev[tableId] || []), data.data]
        }))
      }
      return data
    } catch (error) {
      console.error('Error creating chair:', error)
      return { success: false, error: 'Failed to create chair' }
    }
  }

  const handleUpdateChair = async (chairId: string, tableId: string, updates: Partial<Chair>, skipReload: boolean = false) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/chairs/${chairId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      })

      const data = await response.json()
      if (data.success && !skipReload) {
        // Update local state immediately instead of reloading
        setTableChairs(prev => ({
          ...prev,
          [tableId]: (prev[tableId] || []).map(c =>
            c.id === chairId ? { ...c, ...updates } : c
          )
        }))
      }
      return data
    } catch (error) {
      console.error('Error updating chair:', error)
      return { success: false, error: 'Failed to update chair' }
    }
  }

  const handleDeleteChair = async (chairId: string, tableId: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/chairs/${chairId}`, {
        method: 'DELETE'
      })

      const data = await response.json()
      if (data.success) {
        // Remove chair from state immediately instead of reloading
        setTableChairs(prev => ({
          ...prev,
          [tableId]: (prev[tableId] || []).filter(c => c.id !== chairId)
        }))
      }
      return data
    } catch (error) {
      console.error('Error deleting chair:', error)
      return { success: false, error: 'Failed to delete chair' }
    }
  }

  const handleOrganizeChairs = async (tableId: string) => {
    const table = tables.find(t => t.id === tableId)
    if (!table) return

    setOrganizingChairs(tableId)
    
    try {
      // Delete existing chairs
      const existingChairs = tableChairs[tableId] || []
      for (const chair of existingChairs) {
        await handleDeleteChair(chair.id, tableId)
      }

      // Calculate and create new organized chairs
      const chairPositions = calculateChairPositions(table)
      for (const pos of chairPositions) {
        await handleCreateChair(tableId, {
          x_position: Math.round(pos.x),
          y_position: Math.round(pos.y),
          rotation: Math.round(pos.rotation),
          chair_type: 'standard'
        })
      }
    } catch (error) {
      console.error('Error organizing chairs:', error)
    } finally {
      setOrganizingChairs(null)
    }
  }

  const handleSaveChairChanges = async () => {
    if (Object.keys(unsavedChairChanges).length === 0) return

    setIsSavingChairs(true)
    try {
      // Save all changes
      const savePromises: Promise<any>[] = []
      Object.entries(unsavedChairChanges).forEach(([chairId, changes]) => {
        savePromises.push(
          handleUpdateChair(chairId, changes.tableId, {
            x_position: changes.x_position,
            y_position: changes.y_position
          }, true) // Skip reload to prevent state conflicts
        )
      })

      await Promise.all(savePromises)
      
      // Clear unsaved changes after successful save
      setUnsavedChairChanges({})
      setNotificationMessage({ type: 'success', message: 'Chair positions saved successfully!' })
      setShowNotificationModal(true)
    } catch (error) {
      console.error('Error saving chair changes:', error)
      setNotificationMessage({ type: 'error', message: 'Failed to save chair positions. Please try again.' })
      setShowNotificationModal(true)
    } finally {
      setIsSavingChairs(false)
    }
  }

  const handleDiscardChairChanges = async () => {
    if (Object.keys(unsavedChairChanges).length === 0) return
    
    if (!confirm('Discard all unsaved chair position changes?')) return

    // Reset all chairs to their original positions from the database
    const affectedTableIds = new Set<string>()
    Object.values(unsavedChairChanges).forEach((changes) => {
      affectedTableIds.add(changes.tableId)
    })
    
    if (affectedTableIds.size > 0) {
      await loadAllChairs(Array.from(affectedTableIds), false)
    }
    
    setUnsavedChairChanges({})
  }

  // Calculate automatic chair positions based on table capacity and shape
  const calculateChairPositions = (table: Table): Array<{ x: number; y: number; rotation: number }> => {
    const chairs: Array<{ x: number; y: number; rotation: number }> = []
    const capacity = table.capacity
    const shape = table.shape || 'round'
    const tableWidth = table.shape === 'round' 
      ? (table.capacity <= 2 ? 64 : table.capacity <= 4 ? 80 : table.capacity <= 6 ? 96 : 112)
      : table.width || 80
    const tableHeight = table.shape === 'round'
      ? (table.capacity <= 2 ? 64 : table.capacity <= 4 ? 80 : table.capacity <= 6 ? 96 : 112)
      : table.height || 80
    
    // Chair size and spacing
    const chairSize = 16 // 8px radius * 2
    const spacing = 24 // Space between chairs (increased for better spacing)
    const distanceFromTable = 12 // Distance from table edge
    
    if (shape === 'round') {
      // Circular arrangement for round tables
      const radius = Math.max(tableWidth, tableHeight) / 2 + distanceFromTable + chairSize / 2
      const angleStep = (2 * Math.PI) / capacity
      
      for (let i = 0; i < capacity; i++) {
        const angle = i * angleStep - Math.PI / 2 // Start from top
        const x = Math.cos(angle) * radius
        const y = Math.sin(angle) * radius
        const chairRotation = (angle * 180) / Math.PI + 90 // Face the table
        chairs.push({ x, y, rotation: chairRotation })
      }
    } else if (shape === 'wall') {
      // Chairs only on the accessible side (front) for wall tables
      const accessibleWidth = tableWidth
      const startX = -(accessibleWidth / 2) + spacing / 2
      const endX = (accessibleWidth / 2) - spacing / 2
      const chairSpacing = capacity > 1 ? (endX - startX) / (capacity - 1) : 0
      
      for (let i = 0; i < capacity; i++) {
        const x = capacity > 1 ? startX + i * chairSpacing : 0
        const y = tableHeight / 2 + distanceFromTable + chairSize / 2
        chairs.push({ x, y, rotation: 0 }) // Face forward
      }
    } else {
      // Square/Rectangle: determine if it's square or rectangle
      const isSquare = Math.abs(tableWidth - tableHeight) < 5 // Consider square if difference < 5px
      const isHorizontal = tableWidth > tableHeight
      
      if (isSquare) {
        // Square table: distribute chairs evenly around all 4 sides
        const chairsPerSide = Math.ceil(capacity / 4)
        let chairIndex = 0
        
        // Top side
        if (chairIndex < capacity) {
          const chairsOnTop = Math.min(chairsPerSide, capacity - chairIndex)
          const startX = -tableWidth / 2 + spacing / 2
          const endX = tableWidth / 2 - spacing / 2
          const stepX = chairsOnTop > 1 ? (endX - startX) / (chairsOnTop - 1) : 0
          for (let i = 0; i < chairsOnTop && chairIndex < capacity; i++) {
            chairs.push({
              x: startX + i * stepX,
              y: -tableHeight / 2 - distanceFromTable - chairSize / 2,
              rotation: 180
            })
            chairIndex++
          }
        }
        
        // Right side
        if (chairIndex < capacity) {
          const chairsOnRight = Math.min(chairsPerSide, capacity - chairIndex)
          const startY = -tableHeight / 2 + spacing / 2
          const endY = tableHeight / 2 - spacing / 2
          const stepY = chairsOnRight > 1 ? (endY - startY) / (chairsOnRight - 1) : 0
          for (let i = 0; i < chairsOnRight && chairIndex < capacity; i++) {
            chairs.push({
              x: tableWidth / 2 + distanceFromTable + chairSize / 2,
              y: startY + i * stepY,
              rotation: 270
            })
            chairIndex++
          }
        }
        
        // Bottom side
        if (chairIndex < capacity) {
          const chairsOnBottom = Math.min(chairsPerSide, capacity - chairIndex)
          const startX = -tableWidth / 2 + spacing / 2
          const endX = tableWidth / 2 - spacing / 2
          const stepX = chairsOnBottom > 1 ? (endX - startX) / (chairsOnBottom - 1) : 0
          for (let i = 0; i < chairsOnBottom && chairIndex < capacity; i++) {
            chairs.push({
              x: startX + i * stepX,
              y: tableHeight / 2 + distanceFromTable + chairSize / 2,
              rotation: 0
            })
            chairIndex++
          }
        }
        
        // Left side
        if (chairIndex < capacity) {
          const chairsOnLeft = Math.min(chairsPerSide, capacity - chairIndex)
          const startY = -tableHeight / 2 + spacing / 2
          const endY = tableHeight / 2 - spacing / 2
          const stepY = chairsOnLeft > 1 ? (endY - startY) / (chairsOnLeft - 1) : 0
          for (let i = 0; i < chairsOnLeft && chairIndex < capacity; i++) {
            chairs.push({
              x: -tableWidth / 2 - distanceFromTable - chairSize / 2,
              y: startY + i * stepY,
              rotation: 90
            })
            chairIndex++
          }
        }
      } else {
        // Rectangle: chairs only on the LONG sides
        // const longSide = Math.max(tableWidth, tableHeight)
        // const shortSide = Math.min(tableWidth, tableHeight)
        const chairsPerLongSide = Math.ceil(capacity / 2)
        
        if (isHorizontal) {
          // Horizontal rectangle: chairs on top and bottom (long sides)
          // Top side
          const chairsOnTop = Math.min(chairsPerLongSide, capacity)
          const startX = -tableWidth / 2 + spacing / 2
          const endX = tableWidth / 2 - spacing / 2
          const stepX = chairsOnTop > 1 ? (endX - startX) / (chairsOnTop - 1) : 0
          for (let i = 0; i < chairsOnTop; i++) {
            chairs.push({
              x: startX + i * stepX,
              y: -tableHeight / 2 - distanceFromTable - chairSize / 2,
              rotation: 180
            })
          }
          
          // Bottom side
          const remainingChairs = capacity - chairsOnTop
          if (remainingChairs > 0) {
            const startXBottom = -tableWidth / 2 + spacing / 2
            const endXBottom = tableWidth / 2 - spacing / 2
            const stepXBottom = remainingChairs > 1 ? (endXBottom - startXBottom) / (remainingChairs - 1) : 0
            for (let i = 0; i < remainingChairs; i++) {
              chairs.push({
                x: startXBottom + i * stepXBottom,
                y: tableHeight / 2 + distanceFromTable + chairSize / 2,
                rotation: 0
              })
            }
          }
        } else {
          // Vertical rectangle: chairs on left and right (long sides)
          // Right side
          const chairsOnRight = Math.min(chairsPerLongSide, capacity)
          const startY = -tableHeight / 2 + spacing / 2
          const endY = tableHeight / 2 - spacing / 2
          const stepY = chairsOnRight > 1 ? (endY - startY) / (chairsOnRight - 1) : 0
          for (let i = 0; i < chairsOnRight; i++) {
            chairs.push({
              x: tableWidth / 2 + distanceFromTable + chairSize / 2,
              y: startY + i * stepY,
              rotation: 270
            })
          }
          
          // Left side
          const remainingChairs = capacity - chairsOnRight
          if (remainingChairs > 0) {
            const startYLeft = -tableHeight / 2 + spacing / 2
            const endYLeft = tableHeight / 2 - spacing / 2
            const stepYLeft = remainingChairs > 1 ? (endYLeft - startYLeft) / (remainingChairs - 1) : 0
            for (let i = 0; i < remainingChairs; i++) {
              chairs.push({
                x: -tableWidth / 2 - distanceFromTable - chairSize / 2,
                y: startYLeft + i * stepYLeft,
                rotation: 90
              })
            }
          }
        }
      }
    }
    
    return chairs
  }

  const filteredTables = tables.filter(table => {
    const matchesSearch = table.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         table.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || table.status === statusFilter
    const matchesFloorPlan = floorPlanFilter === 'all' || table.floor_plan_id === floorPlanFilter
    return matchesSearch && matchesStatus && matchesFloorPlan
  })

  // Helper function to get floor plan name by ID
  const getFloorPlanName = (floorPlanId?: string) => {
    if (!floorPlanId) return 'No Floor Plan'
    const floorPlan = floorPlans.find(fp => fp.id === floorPlanId)
    return floorPlan ? floorPlan.name : 'Unknown Floor'
  }

  const getTableStats = () => {
    const total = tables.length
    const available = tables.filter(t => t.status === 'available').length
    const occupied = tables.filter(t => t.status === 'occupied').length
    const reserved = tables.filter(t => t.status === 'reserved').length
    const cleaning = tables.filter(t => t.status === 'cleaning').length
    
    return { total, available, occupied, reserved, cleaning }
  }

  const stats = getTableStats()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading tables...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500">{error}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Table Management</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage tables, reservations, and dining room operations
          </p>
        </div>
        
        <div className="mt-4 lg:mt-0 flex space-x-3">
          <button
            onClick={() => setShowReservationModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Reservation
          </button>
          <button
            onClick={() => setShowTableModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Table
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TableCellsIcon className="h-8 w-8 text-gray-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Tables</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <span className="text-green-600 dark:text-green-400 text-sm font-bold">✓</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Available</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.available}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <span className="text-red-600 dark:text-red-400 text-sm font-bold">🍽️</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Occupied</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.occupied}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 dark:text-yellow-400 text-sm font-bold">📅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Reserved</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.reserved}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-400 text-sm font-bold">🧹</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Cleaning</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.cleaning}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search tables..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="reserved">Reserved</option>
              <option value="cleaning">Cleaning</option>
            </select>
            
            <select
              value={floorPlanFilter}
              onChange={(e) => setFloorPlanFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Floors</option>
              {floorPlans.map(floorPlan => (
                <option key={floorPlan.id} value={floorPlan.id}>
                  {floorPlan.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('floorplan')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'floorplan'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Floor Plan
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md ${
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-400'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              <TableCellsIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md ${
                viewMode === 'list'
                  ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-400'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Floor Plan View */}
      {viewMode === 'floorplan' ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Floor Plan</h3>
                {floorPlans.length > 0 && (
                  <select
                    value={selectedFloorPlanId || ''}
                    onChange={(e) => {
                      setSelectedFloorPlanId(e.target.value)
                      // Reload data when floor plan changes
                      setTimeout(() => {
                        loadTables()
                        loadFloorPlanElements()
                        loadAllChairs(tables.map(t => t.id))
                      }, 100)
                    }}
                    className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    {floorPlans.map(fp => (
                      <option key={fp.id} value={fp.id}>
                        {fp.name} {fp.level !== 0 && `(Level ${fp.level > 0 ? '+' : ''}${fp.level})`}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => {
                    setIsEditingFloorPlan(!isEditingFloorPlan)
                    setDrawingTool('none')
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isEditingFloorPlan
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {isEditingFloorPlan ? 'Exit Edit Mode' : 'Edit Floor Plan'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFloorPlanScale(Math.max(0.5, floorPlanScale - 0.1))}
                  className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm"
                >
                  -
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 w-16 text-center">
                  {Math.round(floorPlanScale * 100)}%
                </span>
                <button
                  onClick={() => setFloorPlanScale(Math.min(2, floorPlanScale + 0.1))}
                  className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm"
                >
                  +
                </button>
              </div>
            </div>
            
            {/* Drawing Tools Toolbar */}
            {isEditingFloorPlan && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Tools:</span>
                <button
                  onClick={() => setDrawingTool('none')}
                  className={`px-3 py-1 rounded text-sm ${
                    drawingTool === 'none'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Select
                </button>
                <button
                  onClick={() => setDrawingTool('table')}
                  className={`px-3 py-1 rounded text-sm ${
                    drawingTool === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Table
                </button>
                <button
                  onClick={() => setDrawingTool('wall')}
                  className={`px-3 py-1 rounded text-sm ${
                    drawingTool === 'wall'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Wall
                </button>
                <button
                  onClick={() => setDrawingTool('door')}
                  className={`px-3 py-1 rounded text-sm ${
                    drawingTool === 'door'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Door
                </button>
                <button
                  onClick={() => setDrawingTool('entry')}
                  className={`px-3 py-1 rounded text-sm ${
                    drawingTool === 'entry'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Entry
                </button>
                <button
                  onClick={() => setDrawingTool('exit')}
                  className={`px-3 py-1 rounded text-sm ${
                    drawingTool === 'exit'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Exit
                </button>
                <button
                  onClick={() => setDrawingTool('chair')}
                  className={`px-3 py-1 rounded text-sm ${
                    drawingTool === 'chair'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Chair
                </button>
              </div>
            )}
            
            {/* Chair Organization Controls */}
            {isEditingFloorPlan && selectedTable && (
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => handleOrganizeChairs(selectedTable.id)}
                  disabled={organizingChairs === selectedTable.id}
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm rounded"
                >
                  {organizingChairs === selectedTable.id ? 'Organizing...' : 'Auto-Organize Chairs'}
                </button>
                <button
                  onClick={() => {
                    // Delete all chairs for this table
                    if (confirm('Delete all chairs for this table?')) {
                      const chairs = tableChairs[selectedTable.id] || []
                      Promise.all(chairs.map(chair => handleDeleteChair(chair.id, selectedTable.id)))
                    }
                  }}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded"
                >
                  Clear All Chairs
                </button>
              </div>
            )}
            
            {/* Save/Discard Chair Changes Controls */}
            {isEditingFloorPlan && Object.keys(unsavedChairChanges).length > 0 && (
              <div className="mt-2 flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                <span className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                  {Object.keys(unsavedChairChanges).length} unsaved chair position{Object.keys(unsavedChairChanges).length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={handleSaveChairChanges}
                  disabled={isSavingChairs}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded"
                >
                  {isSavingChairs ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleDiscardChairChanges}
                  disabled={isSavingChairs}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white text-sm rounded"
                >
                  Discard
                </button>
              </div>
            )}
          </div>
          <div className="relative bg-gray-100 dark:bg-gray-900 overflow-auto" style={{ height: 'calc(100vh - 400px)', minHeight: '600px' }}>
            <div
              data-floor-plan-container
              className="relative"
              style={{
                width: '2000px',
                height: '1500px',
                backgroundImage: `
                  linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px',
                transform: `scale(${floorPlanScale})`,
                transformOrigin: 'top left'
              }}
              onMouseDown={(e) => {
                if (!isEditingFloorPlan || isDragging || e.target !== e.currentTarget) return
                
                const rect = e.currentTarget.getBoundingClientRect()
                const x = (e.clientX - rect.left) / floorPlanScale
                const y = (e.clientY - rect.top) / floorPlanScale
                
                if (drawingTool === 'table') {
                  setNewTable({ x: Math.round(x), y: Math.round(y), capacity: 4, location: 'indoor', shape: 'round' })
                  setShowTableModal(true)
                } else if (['wall', 'door', 'entry', 'exit'].includes(drawingTool)) {
                  setDrawingStart({ x: Math.round(x), y: Math.round(y) })
                }
              }}
              onMouseMove={(e) => {
                if (!drawingStart || !['wall', 'door', 'entry', 'exit'].includes(drawingTool)) return
                const rect = e.currentTarget.getBoundingClientRect()
                const x = (e.clientX - rect.left) / floorPlanScale
                const y = (e.clientY - rect.top) / floorPlanScale
                setDrawingEnd({ x: Math.round(x), y: Math.round(y) })
              }}
              onMouseUp={(e) => {
                if (!drawingStart || !['wall', 'door', 'entry', 'exit'].includes(drawingTool)) return
                
                const rect = e.currentTarget.getBoundingClientRect()
                const x2 = (e.clientX - rect.left) / floorPlanScale
                const y2 = (e.clientY - rect.top) / floorPlanScale
                
                if (Math.abs(x2 - drawingStart.x) > 10 || Math.abs(y2 - drawingStart.y) > 10) {
                  handleCreateFloorPlanElement({
                    type: drawingTool as 'wall' | 'door' | 'entry' | 'exit',
                    x1: drawingStart.x,
                    y1: drawingStart.y,
                    x2: Math.round(x2),
                    y2: Math.round(y2),
                    width: drawingTool === 'wall' ? 10 : drawingTool === 'door' ? 5 : 20,
                    floor_plan_id: selectedFloorPlanId || undefined
                  } as any)
                }
                setDrawingStart(null)
                setDrawingEnd(null)
              }}
            >
              {/* Render Floor Plan Elements (Walls, Doors, Entry/Exit) */}
              {floorPlanElements.map(element => {
                const length = Math.sqrt(Math.pow(element.x2 - element.x1, 2) + Math.pow(element.y2 - element.y1, 2))
                const angle = Math.atan2(element.y2 - element.y1, element.x2 - element.x1) * 180 / Math.PI
                
                return (
                  <div
                    key={element.id}
                    className={`absolute ${
                      element.type === 'wall' ? 'bg-gray-800 dark:bg-gray-600' :
                      element.type === 'door' ? 'bg-amber-600' :
                      element.type === 'entry' ? 'bg-green-500' :
                      'bg-red-500'
                    } ${isEditingFloorPlan ? 'cursor-pointer hover:opacity-80' : ''}`}
                    style={{
                      left: `${element.x1}px`,
                      top: `${element.y1}px`,
                      width: `${length}px`,
                      height: `${element.width || 10}px`,
                      transform: `rotate(${angle}deg) translateY(-${(element.width || 10) / 2}px)`,
                      transformOrigin: 'left center',
                      zIndex: 5
                    }}
                    onClick={() => {
                      if (isEditingFloorPlan && confirm('Delete this element?')) {
                        handleDeleteFloorPlanElement(element.id)
                      }
                    }}
                  >
                    {element.type === 'entry' && (
                      <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">
                        ENTRY
                      </div>
                    )}
                    {element.type === 'exit' && (
                      <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">
                        EXIT
                      </div>
                    )}
                  </div>
                )
              })}
              
              {/* Render Tables with different shapes */}
              {tables.map(table => {
                // Calculate table dimensions
                const tableWidth = table.shape === 'round' 
                  ? (table.capacity <= 2 ? 64 : table.capacity <= 4 ? 80 : table.capacity <= 6 ? 96 : 112)
                  : table.width || 80
                const tableHeight = table.shape === 'round'
                  ? (table.capacity <= 2 ? 64 : table.capacity <= 4 ? 80 : table.capacity <= 6 ? 96 : 112)
                  : table.height || 80
                const rotation = table.rotation || 0

                const handleMouseDown = (e: React.MouseEvent) => {
                  if (!isEditingFloorPlan) return
                  e.preventDefault()
                  e.stopPropagation()
                  setIsDragging(true)
                  setDraggedTable(table.id)
                  const container = document.querySelector('[data-floor-plan-container]') as HTMLElement
                  if (container) {
                    const rect = container.getBoundingClientRect()
                    setDragStartPos({
                      tableId: table.id,
                      startX: table.x,
                      startY: table.y,
                      offsetX: e.clientX - rect.left - table.x * floorPlanScale,
                      offsetY: e.clientY - rect.top - table.y * floorPlanScale
                    })
                  }
                }

                return (
                  <div
                    key={table.id}
                    onMouseDown={handleMouseDown}
                    onClick={(e) => {
                      if (isDragging) return
                      
                      // Don't handle table click if clicking on a chair
                      const target = e.target as HTMLElement
                      if (target.closest('[data-chair-element]') || target.hasAttribute('data-chair-element')) {
                        return
                      }
                      
                      // Check if the click originated from a chair element
                      if ((e.nativeEvent as any).chairClickHandled) {
                        return
                      }
                      
                      if (isEditingFloorPlan && drawingTool === 'chair') {
                        // Add chair to this table (position relative to table center)
                        // const rect = e.currentTarget.getBoundingClientRect()
                        const container = document.querySelector('[data-floor-plan-container]') as HTMLElement
                        if (container) {
                          const containerRect = container.getBoundingClientRect()
                          const absoluteX = (e.clientX - containerRect.left) / floorPlanScale
                          const absoluteY = (e.clientY - containerRect.top) / floorPlanScale
                          // Calculate position relative to table center
                          const x = absoluteX - table.x - tableWidth / 2
                          const y = absoluteY - table.y - tableHeight / 2
                          handleCreateChair(table.id, {
                            x_position: Math.round(x),
                            y_position: Math.round(y),
                            rotation: 0,
                            chair_type: 'standard'
                          })
                        }
                        return
                      }
                      
                      if (!isEditingFloorPlan) {
                        setSelectedTable(table)
                        setShowOrderModal(true)
                      } else if (isEditingFloorPlan) {
                        setSelectedTable(table)
                        setNewTable({
                          number: table.number,
                          capacity: table.capacity,
                          location: table.location,
                          x: table.x,
                          y: table.y,
                          shape: table.shape,
                          width: table.width,
                          height: table.height,
                          rotation: table.rotation
                        })
                        setShowTableModal(true)
                      }
                    }}
                    className={`absolute ${isEditingFloorPlan ? 'cursor-move' : 'cursor-pointer'} transition-all ${
                      selectedTable?.id === table.id ? 'ring-2 ring-blue-500' : ''
                    } ${draggedTable === table.id ? 'opacity-70 z-50' : 'z-10'}`}
                    style={{
                      left: `${table.x}px`,
                      top: `${table.y}px`,
                      transform: `scale(${1 / floorPlanScale})`,
                      transformOrigin: 'top left',
                      pointerEvents: 'auto'
                    }}
                  >
                  {/* Table shape rendering */}
                  <div
                    className={`relative ${
                      table.shape === 'round' ? 'rounded-full' :
                      table.shape === 'square' ? 'rounded-lg' :
                      table.shape === 'rectangle' ? 'rounded-lg' :
                      'rounded-none'
                    } ${
                      table.status === 'available' ? 'bg-green-500' :
                      table.status === 'occupied' ? 'bg-red-500' :
                      table.status === 'reserved' ? 'bg-yellow-500' :
                      'bg-blue-500'
                    } border-4 border-white dark:border-gray-800 shadow-lg flex items-center justify-center group`}
                    style={{
                      width: tableWidth,
                      height: tableHeight,
                      transform: `rotate(${rotation}deg)`
                    }}
                  >
                    <div className="text-center transform -rotate-90" style={{ transform: `rotate(-${rotation}deg)` }}>
                      <div className="text-white font-bold text-sm">{table.number}</div>
                      <div className="text-white text-xs">{table.capacity} seats</div>
                    </div>
                    {table.currentOrder && (
                      <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center z-10">
                        !
                      </div>
                    )}
                    {table.reservation && (
                      <div className="absolute -top-2 -left-2 bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center z-10">
                        📅
                      </div>
                    )}
                    {isEditingFloorPlan && (
                      <div className="absolute inset-0 border-2 border-dashed border-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <PencilIcon className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* Render chairs around table */}
                  {/* First render manually added chairs from database */}
                  {(tableChairs[table.id] || []).map(chair => {
                    // Chairs from DB are stored relative to table center
                    // Apply table rotation if needed
                    const chairRad = (rotation * Math.PI) / 180
                    const rotatedChairX = chair.x_position * Math.cos(chairRad) - chair.y_position * Math.sin(chairRad)
                    const rotatedChairY = chair.x_position * Math.sin(chairRad) + chair.y_position * Math.cos(chairRad)
                    const finalChairRotation = (chair.rotation || 0) + rotation
                    
                    // Determine chair visual style based on type
                    const getChairStyle = () => {
                      switch (chair.chair_type) {
                        case 'sofa':
                          return 'w-12 h-8 bg-purple-600 dark:bg-purple-700 rounded-lg' // Wider, rectangular
                        case 'back_chair':
                          return 'w-8 h-10 bg-blue-600 dark:bg-blue-700 rounded-t-lg' // Taller with back
                        case 'barstool':
                          return 'w-6 h-6 bg-orange-600 dark:bg-orange-700 rounded-full' // Smaller, circular
                        case 'booth':
                          return 'w-16 h-8 bg-indigo-600 dark:bg-indigo-700 rounded-lg' // Very wide
                        default:
                          return 'w-8 h-8 bg-amber-600 dark:bg-amber-700 rounded-full' // Standard
                      }
                    }
                    
                    const isSelected = selectedChair?.chair.id === chair.id && selectedChair?.tableId === table.id
                    const isDragging = draggedChair?.chairId === chair.id && draggedChair?.tableId === table.id
                    const hasUnsavedChanges = unsavedChairChanges[chair.id] !== undefined
                    
                    return (
                      <div
                        key={`db-${chair.id}`}
                        data-chair-element
                        className={`absolute ${getChairStyle()} border-2 ${
                          isSelected ? 'border-blue-500' : 
                          hasUnsavedChanges ? 'border-yellow-500' :
                          'border-white dark:border-gray-800'
                        } shadow-md cursor-move hover:opacity-80 z-30 ${isDragging ? 'opacity-70' : ''}`}
                        style={{
                          left: `${tableWidth / 2 + rotatedChairX}px`,
                          top: `${tableHeight / 2 + rotatedChairY}px`,
                          transform: `scale(${1 / floorPlanScale}) rotate(${finalChairRotation}deg)`,
                          transformOrigin: 'center',
                          pointerEvents: 'auto'
                        }}
                        onMouseDown={(e) => {
                          if (!isEditingFloorPlan) return
                          e.stopPropagation()
                          e.preventDefault()
                          
                          // Calculate the offset from mouse click position to chair center
                          const container = document.querySelector('[data-floor-plan-container]') as HTMLElement
                          if (!container) return
                          
                          const rect = container.getBoundingClientRect()
                          const mouseX = (e.clientX - rect.left) / floorPlanScale
                          const mouseY = (e.clientY - rect.top) / floorPlanScale
                          
                          // Calculate chair's absolute position in floor plan coordinates
                          const chairAbsX = table.x + tableWidth / 2 + rotatedChairX
                          const chairAbsY = table.y + tableHeight / 2 + rotatedChairY
                          
                          // Calculate offset from mouse click to chair center (in floor plan coordinates)
                          const offsetX = mouseX - chairAbsX
                          const offsetY = mouseY - chairAbsY
                          
                          setDraggedChair({
                            chairId: chair.id,
                            tableId: table.id,
                            startX: chair.x_position,
                            startY: chair.y_position,
                            mouseStartX: e.clientX,
                            mouseStartY: e.clientY,
                            offsetX: offsetX,
                            offsetY: offsetY
                          })
                          setChairWasDragged(false)
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          
                          // Mark that chair click was handled to prevent table click
                          ;(e.nativeEvent as any).chairClickHandled = true
                          
                          // Don't open modal if chair was dragged
                          if (chairWasDragged) {
                            return
                          }
                          
                          if (isEditingFloorPlan) {
                            setSelectedChair({ chair, tableId: table.id })
                            setEditingChair({ ...chair })
                            setShowChairModal(true)
                          }
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          if (isEditingFloorPlan && confirm('Delete this chair?')) {
                            handleDeleteChair(chair.id, table.id)
                          }
                        }}
                      />
                    )
                  })}
                  
                  {/* Then render auto-generated chairs based on capacity (only if no manual chairs exist) */}
                  {(!tableChairs[table.id] || tableChairs[table.id].length === 0) && 
                   calculateChairPositions(table).map((chairPos, index) => {
                    // Apply table rotation to chair positions
                    const rad = (rotation * Math.PI) / 180
                    const rotatedX = chairPos.x * Math.cos(rad) - chairPos.y * Math.sin(rad)
                    const rotatedY = chairPos.x * Math.sin(rad) + chairPos.y * Math.cos(rad)
                    const finalRotation = chairPos.rotation + rotation
                    
                    return (
                      <div
                        key={`auto-${table.id}-${index}`}
                        className="absolute w-8 h-8 bg-amber-600 dark:bg-amber-700 rounded-full border-2 border-white dark:border-gray-800 shadow-sm z-20 opacity-90"
                        style={{
                          left: `${tableWidth / 2 + rotatedX}px`,
                          top: `${tableHeight / 2 + rotatedY}px`,
                          transform: `scale(${1 / floorPlanScale}) rotate(${finalRotation}deg)`,
                          transformOrigin: 'center'
                        }}
                      />
                    )
                  })}
                  </div>
                )
              })}
              {/* Drawing preview line */}
              {drawingStart && drawingEnd && ['wall', 'door', 'entry', 'exit'].includes(drawingTool) && (
                <svg className="absolute pointer-events-none z-30" style={{ width: '2000px', height: '1500px' }}>
                  <line
                    x1={drawingStart.x}
                    y1={drawingStart.y}
                    x2={drawingEnd.x}
                    y2={drawingEnd.y}
                    stroke={drawingTool === 'wall' ? '#1f2937' : drawingTool === 'door' ? '#d97706' : drawingTool === 'entry' ? '#10b981' : '#ef4444'}
                    strokeWidth={drawingTool === 'wall' ? 10 : drawingTool === 'door' ? 5 : 20}
                    strokeDasharray="5,5"
                  />
                </svg>
              )}
              
              {isEditingFloorPlan && (
                <div className="absolute bottom-4 left-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm z-40">
                  {drawingTool === 'table' && 'Click on empty space to add a new table'}
                  {drawingTool === 'wall' && 'Click and drag to draw a wall'}
                  {drawingTool === 'door' && 'Click and drag to draw a door'}
                  {drawingTool === 'entry' && 'Click and drag to mark entry point'}
                  {drawingTool === 'exit' && 'Click and drag to mark exit point'}
                  {drawingTool === 'chair' && 'Click on a table to add a chair'}
                  {drawingTool === 'none' && 'Select a tool to start editing'}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTables.map(table => (
            <div
              key={table.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                setSelectedTable(table)
                setShowOrderModal(true)
              }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {table.number}
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(table.status)}`}>
                    {getStatusIcon(table.status)} {table.status}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    Capacity: {table.capacity}
                  </div>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <span className="mr-2">📍</span>
                    {table.location}
                  </div>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <span className="mr-2">🏢</span>
                    {getFloorPlanName(table.floor_plan_id)}
                  </div>
                  
                  {table.currentOrder && (
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Order #{table.currentOrder.id}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          table.currentOrder.status === 'ready' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {table.currentOrder.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Customer: {table.currentOrder.customerName}
                      </div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Total: ${table.currentOrder.total.toFixed(2)}
                      </div>
                      {table.currentOrder.estimatedReadyTime && (
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          Ready in {Math.ceil((table.currentOrder.estimatedReadyTime.getTime() - Date.now()) / 60000)} min
                        </div>
                      )}
                    </div>
                  )}

                  {table.reservation && (
                    <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                      <div className="text-sm font-medium text-blue-900 dark:text-blue-200">
                        Reservation
                      </div>
                      <div className="text-sm text-blue-800 dark:text-blue-300">
                        {table.reservation.customerName}
                      </div>
                      <div className="text-xs text-blue-700 dark:text-blue-400">
                        Party: {table.reservation.partySize} • {table.reservation.reservationTime.toLocaleTimeString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Table
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Capacity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Current Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTables.map(table => (
                  <tr key={table.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {table.number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(table.status)}`}>
                        {getStatusIcon(table.status)} {table.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {table.capacity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {table.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {getFloorPlanName(table.floor_plan_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {table.currentOrder ? (
                        <div>
                          <div className="font-medium">#{table.currentOrder.id}</div>
                          <div className="text-gray-500 dark:text-gray-400">
                            {table.currentOrder.customerName}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">No order</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedTable(table)
                            setShowOrderModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedTable(table)
                            setNewTable({
                              number: table.number,
                              capacity: table.capacity,
                              location: table.location,
                              x: table.x,
                              y: table.y
                            })
                            setShowTableModal(true)
                          }}
                          className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTable(table.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Order Details - {selectedTable.number}
              </h3>
              <button
                onClick={() => setShowOrderModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {selectedTable.currentOrder ? (
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Customer</label>
                      <p className="text-gray-900 dark:text-white">{selectedTable.currentOrder.customerName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</label>
                      <p className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        selectedTable.currentOrder.status === 'ready' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {selectedTable.currentOrder.status}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total</label>
                      <p className="text-gray-900 dark:text-white font-semibold">
                        ${selectedTable.currentOrder.total.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Order Time</label>
                      <p className="text-gray-900 dark:text-white">
                        {selectedTable.currentOrder.createdAt.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Order Items</h4>
                  <div className="space-y-2">
                    {selectedTable.currentOrder.items.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Qty: {item.quantity} × ${item.price.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'ready' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}>
                            {item.status}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            ${(item.quantity * item.price).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowOrderModal(false)}
                    className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  {selectedTable.currentOrder.status === 'ready' && (
                    <button
                      onClick={() => {
                        // Mark order as served
                        setShowOrderModal(false)
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Mark as Served
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <TableCellsIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Active Order</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  This table doesn't have an active order.
                </p>
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Table Modal */}
      {showTableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {selectedTable ? 'Edit Table' : 'Add New Table'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Table Number *
                </label>
                <input
                  type="text"
                  value={newTable.number || ''}
                  onChange={(e) => setNewTable({ ...newTable, number: e.target.value })}
                  disabled={!!selectedTable}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                  placeholder="e.g., T07"
                />
                {selectedTable && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Table number cannot be changed
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Capacity *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={newTable.capacity || ''}
                    onChange={(e) => setNewTable({ ...newTable, capacity: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="4"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={selectedTable?.status || newTable.status || 'available'}
                    onChange={(e) => {
                      if (selectedTable) {
                        setNewTable({ ...newTable, status: e.target.value as any })
                      }
                    }}
                    disabled={!selectedTable}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  >
                    <option value="available">Available</option>
                    <option value="occupied">Occupied</option>
                    <option value="reserved">Reserved</option>
                    <option value="cleaning">Cleaning</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location *
                </label>
                <select
                  value={newTable.location || ''}
                  onChange={(e) => setNewTable({ ...newTable, location: e.target.value as 'indoor' | 'outdoor' | 'bar' })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.name}>{loc.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Floor Plan *
                </label>
                <select
                  value={newTable.floor_plan_id || selectedFloorPlanId || ''}
                  onChange={(e) => setNewTable({ ...newTable, floor_plan_id: e.target.value || undefined })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select Floor Plan</option>
                  {floorPlans.map(fp => (
                    <option key={fp.id} value={fp.id}>
                      {fp.name} {fp.level !== 0 && `(Level ${fp.level > 0 ? '+' : ''}${fp.level})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Table Shape
              </label>
              <select
                value={newTable.shape || 'round'}
                onChange={(e) => setNewTable({ ...newTable, shape: e.target.value as 'round' | 'square' | 'rectangle' | 'wall' })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="round">Round</option>
                <option value="square">Square</option>
                <option value="rectangle">Rectangle</option>
                <option value="wall">Wall Table</option>
              </select>
            </div>

            {(newTable.shape === 'square' || newTable.shape === 'rectangle' || newTable.shape === 'wall') && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Width (px)
                  </label>
                  <input
                    type="number"
                    min="40"
                    max="200"
                    value={newTable.width || 80}
                    onChange={(e) => setNewTable({ ...newTable, width: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Height (px)
                  </label>
                  <input
                    type="number"
                    min="40"
                    max="200"
                    value={newTable.height || 80}
                    onChange={(e) => setNewTable({ ...newTable, height: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rotation (°)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="360"
                    value={newTable.rotation || 0}
                    onChange={(e) => setNewTable({ ...newTable, rotation: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowTableModal(false)
                  setSelectedTable(null)
                  setNewTable({})
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedTable) {
                    handleUpdateTable({ ...selectedTable, ...newTable } as Table)
                  } else {
                    handleAddTable()
                  }
                }}
                disabled={isSaving}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {isSaving ? (selectedTable ? 'Updating...' : 'Adding...') : (selectedTable ? 'Update Table' : 'Add Table')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chair Edit Modal */}
      {showChairModal && selectedChair && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Chair
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Chair Type *
                </label>
                <select
                  value={editingChair.chair_type || 'standard'}
                  onChange={(e) => setEditingChair({ ...editingChair, chair_type: e.target.value as any })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="standard">Standard Chair</option>
                  <option value="sofa">Sofa</option>
                  <option value="back_chair">Back Chair</option>
                  <option value="barstool">Bar Stool</option>
                  <option value="booth">Booth</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    X Position
                  </label>
                  <input
                    type="number"
                    value={editingChair.x_position || 0}
                    onChange={(e) => setEditingChair({ ...editingChair, x_position: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Y Position
                  </label>
                  <input
                    type="number"
                    value={editingChair.y_position || 0}
                    onChange={(e) => setEditingChair({ ...editingChair, y_position: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rotation (degrees)
                </label>
                <input
                  type="number"
                  min="0"
                  max="360"
                  value={editingChair.rotation || 0}
                  onChange={(e) => setEditingChair({ ...editingChair, rotation: Number(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => setEditingChair({ ...editingChair, rotation: ((editingChair.rotation || 0) - 90 + 360) % 360 })}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm"
                  >
                    Rotate -90°
                  </button>
                  <button
                    onClick={() => setEditingChair({ ...editingChair, rotation: ((editingChair.rotation || 0) + 90) % 360 })}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm"
                  >
                    Rotate +90°
                  </button>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowChairModal(false)
                  setSelectedChair(null)
                  setEditingChair({})
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedChair) {
                    await handleUpdateChair(selectedChair.chair.id, selectedChair.tableId, editingChair)
                    setShowChairModal(false)
                    setSelectedChair(null)
                    setEditingChair({})
                  }
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={async () => {
                  if (selectedChair && confirm('Delete this chair?')) {
                    await handleDeleteChair(selectedChair.chair.id, selectedChair.tableId)
                    setShowChairModal(false)
                    setSelectedChair(null)
                    setEditingChair({})
                  }
                }}
                className="px-4 bg-red-600 hover:bg-red-700 text-white font-medium py-2 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Reservation Modal */}
      {showReservationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              New Reservation
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Name *
                </label>
                <input
                  type="text"
                  value={newReservation.customerName || ''}
                  onChange={(e) => setNewReservation({ ...newReservation, customerName: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={newReservation.customerPhone || ''}
                  onChange={(e) => setNewReservation({ ...newReservation, customerPhone: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="+966501234567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Party Size *
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={newReservation.partySize || ''}
                  onChange={(e) => setNewReservation({ ...newReservation, partySize: Number(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="4"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date *
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {dateRange.map((d) => {
                    const closed = isDateClosed(d, restaurantSchedule, restaurantHolidays)
                    const isSelected = reservationDate && reservationDate.getTime() === d.getTime()
                    return (
                      <button
                        key={d.getTime()}
                        type="button"
                        onClick={() => {
                          if (closed) return
                          const daySched = getScheduleForDay(restaurantSchedule, d.getDay())
                          const slots = buildTimeSlots(slotDurationMinutes, daySched.open, daySched.close)
                          const firstAvailable = slots.find((slot) => !isSlotTooSoon(getSlotDateTime(d, slot, daySched), minimumBookingHours)) || slots[0] || '16:00'
                          setNewReservationDateAndTime(d, firstAvailable)
                        }}
                        disabled={closed}
                        className={`px-2 py-1 rounded text-sm ${closed ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : isSelected ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900'}`}
                      >
                        {DAY_NAMES[d.getDay()]}, {d.getDate()} {d.toLocaleDateString(undefined, { month: 'short' })}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time *
                </label>
                {!reservationDate ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Select a date first.</p>
                ) : selectedDateClosed ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Closed on this day.</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {(timeSlotsForSelectedDate.includes(reservationTimeSlot) ? timeSlotsForSelectedDate : [...timeSlotsForSelectedDate, reservationTimeSlot].filter(Boolean)).map((slot) => {
                      const slotDt = selectedDaySchedule ? getSlotDateTime(reservationDate, slot, selectedDaySchedule) : null
                      const tooSoon = slotDt ? isSlotTooSoon(slotDt, minimumBookingHours) : false
                      const isSelected = reservationTimeSlot === slot
                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => !tooSoon && setNewReservationDateAndTime(reservationDate, slot)}
                          disabled={tooSoon}
                          className={`py-2 rounded text-sm font-medium ${
                            tooSoon
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                              : isSelected
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900'
                          }`}
                          title={tooSoon ? `Book at least ${minimumBookingHours} hour(s) ahead` : undefined}
                        >
                          {slot}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Table
                </label>
                <select
                  value={newReservation.tableId || ''}
                  onChange={(e) => setNewReservation({ ...newReservation, tableId: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select Table</option>
                  {tables.filter(t => t.status === 'available').map(table => (
                    <option key={table.id} value={table.id}>
                      {table.number} ({table.capacity} seats) - {table.location || 'No location'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  value={newReservation.notes || ''}
                  onChange={(e) => setNewReservation({ ...newReservation, notes: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Special requests, dietary restrictions, etc."
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowReservationModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddReservation}
                disabled={isSaving}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {isSaving ? 'Creating...' : 'Create Reservation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reservation Success Modal */}
      {showReservationSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
              Reservation Created Successfully!
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              The reservation has been created and the table status has been updated.
            </p>
            <button
              onClick={() => setShowReservationSuccessModal(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Reservation Error Modal */}
      {reservationError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <XMarkIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
              Reservation Failed
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              {reservationError}
            </p>
            <button
              onClick={() => setReservationError(null)}
              className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Notification Modal (success/error) */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                notificationMessage.type === 'success'
                  ? 'bg-green-100 dark:bg-green-900'
                  : 'bg-red-100 dark:bg-red-900'
              }`}>
                {notificationMessage.type === 'success' ? (
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="ml-4 flex-1">
                <h3 className={`text-lg font-medium ${
                  notificationMessage.type === 'success'
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {notificationMessage.type === 'success' ? 'Success' : 'Error'}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {notificationMessage.message}
                </p>
              </div>
              <button
                onClick={() => setShowNotificationModal(false)}
                className="ml-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowNotificationModal(false)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  notificationMessage.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableManagement

